import React, { useEffect, useState } from 'react';
import T from 'prop-types';
// redux
import { connect } from 'react-redux';
import { setTableParams } from 'reducers/tableParams';
import { setBreadcrumbs } from 'reducers/breadcrumbs';
import { openPortalRight } from 'reducers/rightToLeftPortal';
import { setPageOptions } from 'reducers/layout';
import { createUserFilterAction, updateUserFilterAction } from 'reducers/userFilters';
// hooks
import useDataTable, { tableParamsSelector } from 'hooks/useDataTable';
import { useDropdownActions } from 'hooks/useDropdownContext';
// components
import { FormattedMessage, useIntl } from 'react-intl';
import Link from 'react-router/lib/Link';
import BarnSheetsContainer from '../BarnSheetsContainer';
import SearchBox from 'components/SearchBox';
import NothingBox from 'components/NothingBox';
import TableFilter from 'components/TableFilter';
import FarmTypeLabel from 'components/FarmTypeLabel/FarmTypeLabel';
import DataTable from 'components/DataTable/DataTable';
import CustomColumn from 'components/DataTable/Columns/CustomColumn';
import ProgressPercent from 'components/ProgressBar/ProgressPercent';
import ArrowColumn from 'components/DataTable/Columns/ArrowColumn';
import Preloader from 'components/Preloader';
import Panel from 'components/Panel';
import UserFilter from 'components/UserFilter';
import UserFilterMobile from 'components/UserFilterMobile';
import UserFilterMobileHeader from 'components/UserFilterMobile/UserFilterMobileHeader';
import ManageFieldsButton from 'components/ManageFieldsButton';
// requests
import { fetchBarnSheetsFarms, applyBarnSheetsFarmsFilter } from 'endpoints/barnsheets/farms';
// utils
import { getOrderedColumns } from '../BarnSheetsManageColumns/columnsHelper';
import cn from 'classnames';
import useDidMountEffect from 'hooks/useDidMountEffect';
import { useMediaQuery } from 'react-responsive';
import { isTablet } from 'react-device-detect';
import { toastResponseErrors } from 'utils/responseErrorsHelper';
import { formatMortality, formatNumber, formatTableValue, formatWater } from 'utils';
import { tableNames } from 'utils/constants';
import { allOrAnyFarmsOptions, userFilterGroupNames, userFilterSectionTypes } from 'utils/constants/userFilters';
import mixpanel from 'mixpanel-browser';
import HintTooltip from 'components/HintTooltip';

const tableFilters = [
  { label: <FormattedMessage id="general.status.active" />, value: 'active' },
  { label: <FormattedMessage id="general.status.disabled" />, value: 'disabled' },
  { label: <FormattedMessage id="general.allFarms" />, value: '' },
];

const BarnSheetsFarms = ({
  isAdmin,
  isAbfEnabled,
  openPortalRight,
  setTableParams,
  setPageOptions,
  setBreadcrumbs,
  tableParams,
  createUserFilterAction,
  updateUserFilterAction,
  currentUser,
}) => {
  const {
    data: { resources, isLoading, meta },
    fetchData,
    onPageChange,
    onPerPageChange,
    onSortChange,
    onStatusChange,
    onSearchChange,
    onUserFilterSectionsChange,
    onUserFilterSegmentSave,
  } = useDataTable(fetchBarnSheetsFarms, {
    setTableParams: (params) => setTableParams(tableNames.barnSheetsFarms, params),
    tableParams,
    filterFetcher: applyBarnSheetsFarmsFilter,
    userFilterGroupName: userFilterGroupNames.barnSheetsFarms,
    createUserFilter: createUserFilterAction,
    updateUserFilter: updateUserFilterAction,
  });
  const { openDropdown } = useDropdownActions();
  const { formatMessage } = useIntl();
  const { page, per_page, search, sort, status, filters } = tableParams;
  const { total, stats } = meta;
  const isDesktop = useMediaQuery({ minWidth: 1024 });
  const isMetric = currentUser?.current_company?.measurement_system === 'metric';
  const waterUsageLabelId = isMetric
    ? 'component.dataTable.headers.currentLitersConsumed'
    : 'component.dataTable.headers.currentGallonsConsumed';
  const userFilter = filters?.[0];
  const filterSectionsCount = userFilter?.sections?.length;
  const hasMobileFilterHeader = !isDesktop && filterSectionsCount > 0;
  const [initialized, setInitialized] = useState(false);

  const sections = [
    { value: userFilterSectionTypes.company, label: formatMessage({ id: 'general.companyName' }) },
    { value: userFilterSectionTypes.farm, label: formatMessage({ id: 'general.farmName' }) },
    { value: userFilterSectionTypes.farmType, label: formatMessage({ id: 'general.farmType' }) },
    { value: userFilterSectionTypes.pigsCount, label: formatMessage({ id: 'general.pageTitle.inventory' }) },
    { value: userFilterSectionTypes.openPigGroupsCount, label: formatMessage({ id: 'general.openGroups' }) },
    { value: userFilterSectionTypes.totalDeaths,
      label: formatMessage({ id: 'component.dataTable.headers.totalMortality' }) },
    { value: userFilterSectionTypes.avgMortalityRate, label: formatMessage({ id: 'general.mortalityRate' }) },
    { value: userFilterSectionTypes.avgTreated, label: formatMessage({ id: 'general.treated24Hours' }) },
    { value: userFilterSectionTypes.compliance, label: formatMessage({ id: 'general.compliance' }) },
    { value: userFilterSectionTypes.currentWaterUsage,
      label: formatMessage({ id: waterUsageLabelId }) },
  ];

  const handleOpenMobileFilters = () => {
    openPortalRight(
      <UserFilterMobile
        allOrAnyConditionLabel={<FormattedMessage id="component.advancedSearchItem.allFarmsFilters" />}
        appliedFilter={userFilter || {}}
        filterSections={sections}
        filterGroupName={userFilterGroupNames.barnSheetsFarms}
        setFilter={(filter) => fetchData({ ...tableParams, filters: filter ? [filter] : undefined })}
      />,
    );
  };

  const handleSetPageOptions = () => {
    setPageOptions({
      rightButton: !isDesktop ? {
        icon: 'fa fa-filter',
        onClick: handleOpenMobileFilters,
      } : '',
    });
  };

  useDidMountEffect(handleSetPageOptions, [tableParams, isDesktop]);

  useEffect(() => {
    const defaultStatus = 'active';
    const shouldApplyDefault = (
      tableParams.status === undefined
      || tableParams.status === null
      || tableParams.status === ''
    );
    const initialStatus = shouldApplyDefault ? defaultStatus : tableParams.status;
    if (shouldApplyDefault) {
      setTableParams({ ...tableParams, status: initialStatus });
    }
    fetchData({
      ...tableParams,
      status: initialStatus,
      sort: sort || 'active desc',
    })
      .catch(toastResponseErrors);
    setBreadcrumbs([
      {
        label: <FormattedMessage id="general.pageTitle.barnsheets" />,
        path: '/barnsheets/farms',
        useLabelAsMobileTitle: true,
      },
    ]);
    handleSetPageOptions();
    return () => {
      setPageOptions({ rightButton: '' });
    };
  }, []);
  useEffect(() => {
    if (tableParams.status !== undefined && tableParams.status !== null && tableParams.status !== '') {
      setInitialized(true);
    }
  }, [tableParams.status]);

  const isStatusEmpty = status === undefined || status === null || status === '';
  const visibleStatus = (!initialized && isStatusEmpty) ? 'active' : status;

  const handleOpenDropdown = (e, id) => {
    if (currentUser && currentUser.id) {
      mixpanel.track('Feature Used', {
        user_id: currentUser.id,
        feature_name: 'BarnSheetsFarms Dropdown',
        action: 'Open Dropdown',
        farm_id: id,
        timestamp: new Date().toISOString()
      });
    }
    const options = [
      { label: <FormattedMessage id="general.viewBarnSheet" />, url: `/barnsheets/farms/${id}/groups` },
      { label: <FormattedMessage id="general.manageFarm" />, url: `/admin/farms/${id}`, hide: !isAdmin },
    ];
    openDropdown(e, options);
  };

  const renderFarmNameColumn = (rowData) => (
    <CustomColumn label={<FormattedMessage id="component.dataTable.headers.farmName" />}>
      <Link
        to={`/barnsheets/farms/${rowData.id}/groups`}
        className="color-primary"
        onClick={() => {
          if (currentUser && currentUser.id) {
            mixpanel.track('Feature Used', {
              user_id: currentUser.id,
              feature_name: 'BarnSheetsFarms Table',
              action: 'Click View BarnSheet',
              farm_id: rowData.id,
              farm_name: rowData.name,
              timestamp: new Date().toISOString()
            });
          }
        }}
      >
        {rowData.name}
      </Link>
    </CustomColumn>
  );

  const renderFarmTypeColumn = (rowData) => (
    <CustomColumn label={<FormattedMessage id="general.type" />}>
      <FarmTypeLabel farmType={rowData.farm_type} />
    </CustomColumn>
  );

  const renderStartDateColumn = (rowData) => (
    <CustomColumn textRight label={<FormattedMessage id="general.openGroups" />}>
      {formatTableValue(rowData.open_pig_groups_count)}
    </CustomColumn>
  );

  const renderInventoryColumn = (rowData) => (
    <CustomColumn textRight label={<FormattedMessage id="component.dataTable.headers.inventory" />}>
      {formatTableValue(rowData.pigs_count, formatNumber(rowData.pigs_count || 0))}
    </CustomColumn>
  );

  const renderMortalityColumn = (rowData) => (
    <CustomColumn textRight label={<FormattedMessage id="component.dataTable.headers.mortRate" />}>
      {formatMortality(rowData.avg_mortality_rate)}
    </CustomColumn>
  );

  const renderTotalMortalityColumn = (rowData) => {
    const {
      acute_death_count,
      chronic_death_count,
      euthanasia_death_count,
      treated_death_count,
      acute_deaths,
      chronic_deaths,
      euthanasia_deaths,
      treated_deaths,
      total_deaths,
    } = rowData || {};

    const acute = acute_death_count ?? acute_deaths ?? 0;
    const chronic = chronic_death_count ?? chronic_deaths ?? 0;
    const euthanasia = euthanasia_death_count ?? euthanasia_deaths ?? 0;
    const treated = treated_death_count ?? treated_deaths ?? 0;

    return (
      <CustomColumn textRight label={<FormattedMessage id="component.dataTable.headers.totalMortality" />}>
        {total_deaths !== null && total_deaths !== undefined ? (
          <span className="position-relative">
            {total_deaths}
            <HintTooltip attachToBody showBelow arrowed>
              <div className="bs-mortality-tooltip">
                <div><FormattedMessage id="general.deaths.acute" />: {acute}</div>
                <div><FormattedMessage id="general.deaths.chronic" />: {chronic}</div>
                <div><FormattedMessage id="general.deaths.euthanasia" />: {euthanasia}</div>
                {isAbfEnabled && (
                  <div><FormattedMessage id="general.treated" />: {treated}</div>
                )}
              </div>
            </HintTooltip>
          </span>
        ) : '-'}
      </CustomColumn>
    );
  };

  const renderProgressColumn = (rowData) => (
    <CustomColumn textRight label={<FormattedMessage id="component.dataTable.headers.compliance" />} noBottomBorder>
      {formatTableValue(rowData.progress, <ProgressPercent withoutBar progress={rowData.progress} />)}
    </CustomColumn>
  );

  const renderTreatedColumn = (rowData) => {
    const { avg_treated_24h } = rowData;
    return (
      <CustomColumn textRight label={<FormattedMessage id="general.treatedPeriod" values={{ period: '24HR' }} />}>
        {formatTableValue(avg_treated_24h, Number(avg_treated_24h).toFixed(2) + '%', '-')}
      </CustomColumn>
    );
  };

  const formatWaterUsageValue = (value) => (isMetric ? formatWater(value, 'metric') : value);

  const renderWaterUsageColumn = (rowData) => (
    <CustomColumn textRight label={<FormattedMessage id={waterUsageLabelId} />}>
      {rowData.current_water_usage ? formatWaterUsageValue(+rowData.current_water_usage) : '-'}
    </CustomColumn>
  );

  const renderButtonsColumn = (rowData, { rowIndex }) => {
    const { id } = rowData;
    return (
      <div className="collapsible-value button-column">
        <i
          data-name={`${id}-${rowIndex}`}
          className="fa fa-dots-three-horizontal show-for-large"
          onClick={(e) => handleOpenDropdown(e, id)}
        />
        <Link
          to={`/barnsheets/farms/${id}/groups`}
          className="button light hide-for-large"
          onClick={() => {
            if (currentUser && currentUser.id) {
              mixpanel.track('Feature Used', {
                user_id: currentUser.id,
                feature_name: 'BarnSheetsFarms Mobile',
                action: 'Click View BarnSheet',
                farm_id: id,
                timestamp: new Date().toISOString()
              });
            }
          }}
        >
          <FormattedMessage id="general.button.view" />
        </Link>
        {isAdmin && (
          <Link
            to={`/admin/farms/${id}`}
            className="button dark-grey hide-for-large"
            onClick={() => {
              if (currentUser && currentUser.id) {
                mixpanel.track('Feature Used', {
                  user_id: currentUser.id,
                  feature_name: 'BarnSheetsFarms Mobile',
                  action: 'Click Manage Farm',
                  farm_id: id,
                  timestamp: new Date().toISOString()
                });
              }
            }}
          >
            <FormattedMessage id="general.manageFarm" />
          </Link>
        )}
      </div>
    );
  };

  const renderExpandable = (rowData) => {
    const { id } = rowData;
    return (
      <div>
        {isAdmin && (
          <Link
            to={`/admin/farms/${id}`}
            className="button small light"
            onClick={() => {
              if (currentUser && currentUser.id) {
                mixpanel.track('Feature Used', {
                  user_id: currentUser.id,
                  feature_name: 'BarnSheetsFarms Expandable',
                  action: 'Click Manage Farm',
                  farm_id: id,
                  timestamp: new Date().toISOString()
                });
              }
            }}
          >
            <i className="fa fa-gear mr-5" />
            <FormattedMessage id="general.manage" />
          </Link>
        )}
        <Link
          to={`/barnsheets/farms/${id}/groups`}
          className="button small light"
          onClick={() => {
            if (currentUser && currentUser.id) {
              mixpanel.track('Feature Used', {
                user_id: currentUser.id,
                feature_name: 'BarnSheetsFarms Expandable',
                action: 'Click View BarnSheet',
                farm_id: id,
                timestamp: new Date().toISOString()
              });
            }
          }}
        >
          <i className="fa fa-eye mr-5" />
          <FormattedMessage id="general.viewBarnSheet" />
        </Link>
      </div>
    );
  };

  const columns = [
    { id: 'farmName', label: <FormattedMessage id="component.dataTable.headers.farmName" />, flex: '1 1 140px',
      renderer: renderFarmNameColumn, sortKey: 'name' },
    { id: 'type', label: <FormattedMessage id="general.type" />, flex: '1 1 50px', renderer: renderFarmTypeColumn,
      sortKey: 'farm_type' },
    { id: 'openGroups', label: <FormattedMessage id="general.openGroups" />, flex: '1 1 95px',
      renderer: renderStartDateColumn, sortKey: 'open_pig_groups_count', textRight: true },
    { id: 'inventory', label: <FormattedMessage id="component.dataTable.headers.inventory" />, flex: '1 1 70px',
      renderer: renderInventoryColumn, sortKey: 'pigs_count', textRight: true },
    { id: 'mortRate', label: <FormattedMessage id="component.dataTable.headers.mortRate" />, flex: '1 1 100px',
      renderer: renderMortalityColumn, sortKey: 'avg_mortality_rate', textRight: true },
    { id: 'totalMortality', label: <FormattedMessage id="component.dataTable.headers.totalMortality" />,
      flex: '1 1 120px', renderer: renderTotalMortalityColumn, sortKey: 'total_deaths', textRight: true },
    { id: 'treatedPeriod', label: <FormattedMessage id="general.treatedPeriod" values={{ period: '24HR' }} />,
      flex: '1 1 120px', renderer: renderTreatedColumn, textRight: true },
    { id: 'waterUsage', label: <FormattedMessage id={waterUsageLabelId} />,
      flex: '1 1 120px', renderer: renderWaterUsageColumn, sortKey: 'current_water_usage', textRight: true },
    { id: 'compliance', label: <FormattedMessage id="component.dataTable.headers.compliance" />, flex: '1 1 90px',
      renderer: renderProgressColumn, sortKey: 'progress', textRight: true },
    { label: '', flex: '0 0 45px', renderer: renderButtonsColumn, fixed: true,
      className: isTablet ? 'hide-for-large' : '' },
    { label: '', flex: '0 0 45px', renderer: () => <ArrowColumn />, fixed: true, hide: !isTablet, hasPinnedIcon: true },
  ];
  const visibleColumns = getOrderedColumns(columns, tableNames.barnSheetsFarms);

  const paginationProps = {
    onPageChange,
    onPerPageChange,
    totalItems: total,
    currentPage: page,
    perPage: per_page,
  };

  return (
    <BarnSheetsContainer
      className={cn('barn-sheets', { 'is-desktop': isDesktop })}
      headerContent={hasMobileFilterHeader && (
        <UserFilterMobileHeader
          onClick={handleOpenMobileFilters}
          onClose={() => fetchData({ ...tableParams, filters: undefined })}
          sectionsCount={filterSectionsCount}
          segmentName={userFilter?.name}
        />
      )}
    >
      {isDesktop && (
        <UserFilter
          isVisible
          filterGroupName={userFilterGroupNames.barnSheetsFarms}
          availFilterSections={sections}
          className="bs-filters"
          listOptions={allOrAnyFarmsOptions}
          onSectionsChange={onUserFilterSectionsChange}
          initialFilter={tableParams.filters?.[0]}
          onSave={onUserFilterSegmentSave}
        />
      )}
      <Panel className="bs-table-panel panel-padding">
        <Panel.Heading
          renderTitle={() => <h2 className="lighter show-for-large"><FormattedMessage id="general.farms" /></h2>}
        >
          <div className="bs-heading-controls">
            <SearchBox initialValue={search} onChange={onSearchChange} />
            <ManageFieldsButton name={tableNames.barnSheetsFarms} />
          </div>
        </Panel.Heading>
        <Panel.Body noPadding>
          <Preloader isActive={isLoading} />

          <TableFilter
            filters={tableFilters}
            onFilterChange={onStatusChange}
            activeFilter={visibleStatus}
            stats={stats}
            className="mh-10"
          />

          <DataTable
            data={resources}
            columns={visibleColumns}
            sort={sort}
            onSortChange={onSortChange}
            paginationProps={paginationProps}
            isExpandable={isTablet}
            isLoading={isLoading}
            renderExpandable={renderExpandable}
            scrollable
          />

          <NothingBox
            itemsName="barnsheets"
            display={!resources.length}
            isLoading={isLoading}
            search={search}
          >
            <h2 className="lighter"><FormattedMessage id="component.nothingBox.noBarnSheets" /></h2>
          </NothingBox>
        </Panel.Body>
      </Panel>
    </BarnSheetsContainer>
  );
};

BarnSheetsFarms.propTypes = {
  isAdmin: T.bool,
  isAbfEnabled: T.bool.isRequired,
  openPortalRight: T.func.isRequired,
  setBreadcrumbs: T.func.isRequired,
  setPageOptions: T.func.isRequired,
  setTableParams: T.func.isRequired,
  tableParams: T.object.isRequired,
  createUserFilterAction: T.func.isRequired,
  updateUserFilterAction: T.func.isRequired,
  currentUser: T.object,
};

export default connect(
  (state) => ({
    tableParams: tableParamsSelector(state, tableNames.barnSheetsFarms),
    isAdmin: state.auth.user.roles_map.admin,
    isAbfEnabled: state.auth.user.current_company.abf_tracking,
    currentUser: state.auth.user,
  }), {
    openPortalRight,
    setBreadcrumbs,
    setPageOptions,
    setTableParams,
    createUserFilterAction,
    updateUserFilterAction,
  }
)(BarnSheetsFarms);
